import { TransactionCurrency } from '@contracts/payment/Currency';
import { yupResolver } from '@hookform/resolvers/yup';
import { AvailableCurrencyDropdownWithOptionalDefault } from '@src/clients/financialSettings/AvailableCurrencyDropdownWithOptionalDefault';
import { CurrencyField } from '@src/clients/financialSettings/fields/CurrencyFields';
import { useNuveiCreditCardForm } from '@src/clients/financialSettings/hooks/useNuveiCreditCardForm';
import { NewCreditCardFormContent } from '@src/clients/financialSettings/NewCreditCardFormContent';
import { useCreditCards } from '@src/clients/hooks/useCreditCards';
import { Button } from '@src/common/components/button/Button';
import { Divider } from '@src/common/components/Divider';
import { LoadingOverlay } from '@src/common/components/LoadingOverlay';
import { Stack } from '@src/common/components/Stack';
import { ToastLevel } from '@src/common/components/ToastLevel';
import { Toggle } from '@src/common/components/Toggle';
import { Typography } from '@src/common/components/Typography';
import { useToast } from '@src/common/hooks/useToast';
import { theme } from '@src/common/styles/Theme';
import { CreditCardSelector } from '@src/orders/components/preauthorization/CreditCardSelector';
import type { CreditCardRelatedFields } from '@src/orders/fields/CreditCardRelatedFields';
import { CreditCardField } from '@src/orders/fields/CreditCardRelatedFields';
import { AmountField } from '@src/orders/fields/PreauthorizePaymentFields';
import { usePreauthorize } from '@src/orders/hooks/usePreauthorize';
import { useRefreshOrder } from '@src/orders/hooks/useRefreshOrder';
import { ControllerFormNumberInput } from '@src/orders/PermitFormSections/ControllerFormNumberInput';
import { PreauthorizationInputsSchema } from '@src/orders/schemas/PreauthorizationInputsSchema';
import { useCallback, useMemo, useState } from 'react';
import type { Resolver } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface Props {
  clientId: string;
  orderId: string;
  onClose: () => void;
  blockedCurrency?: TransactionCurrency;
}

export const PreauthorizationForm = ({
  clientId,
  orderId,
  onClose,
  blockedCurrency,
}: Props) => {
  const { t } = useTranslation();

  const [useSingleUseCard, setUseSingleUseCard] = useState(false);

  const {
    control,
    register,
    formState,
    reset,
    watch,
    resetField,
    getValues,
    setValue,
  } = useForm({
    resolver: yupResolver(
      PreauthorizationInputsSchema({ useSingleUseCard }),
    ) as unknown as Resolver<CreditCardRelatedFields>,
    mode: 'onChange',
  });

  const currencyField = watch(CurrencyField.fieldName);
  const currency: TransactionCurrency | undefined = useMemo(
    () => (currencyField ? TransactionCurrency[currencyField] : undefined),
    [currencyField],
  );
  const { availableCreditCards } = useCreditCards({
    clientId,
    currency,
  });

  const { showToast } = useToast();
  const { refreshOrder } = useRefreshOrder({ id: orderId });

  const resetForm = () => {
    onClose();
    reset();
  };

  const onPreauthorizationSuccess = (preauthorizedAmount: number) => {
    resetForm();
    showToast({
      message: t('preauthorization.preauthorizationSuccess', {
        amount: preauthorizedAmount,
      }),
      severity: ToastLevel.success,
    });
    refreshOrder();
  };

  const onSaveError = useCallback(
    (message?: string) =>
      showToast({
        message: message ?? t('creditCardSave.error'),
        severity: ToastLevel.error,
      }),
    [showToast, t],
  );

  const onPreAuthFailed = useCallback(
    (message: string) =>
      showToast({
        message,
        severity: ToastLevel.error,
        closesAutomatically: false,
      }),
    [showToast],
  );

  const onPreAuthFailedButCardSaved = ({
    errorCode,
    description,
  }: {
    description: string;
    errorCode: string;
  }) =>
    showToast({
      message: t('preauthorization.preauthFailedButCardSaved', {
        errorCode,
        description,
      }),
      severity: ToastLevel.error,
      closesAutomatically: false,
    });

  const { preauthorize, isLoading: isPreauthorizing } = usePreauthorize({
    clientId,
    orderId,
    onSuccess: onPreauthorizationSuccess,
    onError: onPreAuthFailed,
  });

  const {
    preauthorizeWithNewCard,
    isLoading: nuveiLoading,
    isComplete: isNuveiNewCreditCardFormComplete,
  } = useNuveiCreditCardForm({
    clientId,
    onError: onSaveError,
    currency,
  });

  const preauthorizePayment = async (fields: CreditCardRelatedFields) => {
    if (useSingleUseCard) {
      void preauthorizeWithNewCard({
        creditCardFields: fields,
        onSuccess: onPreauthorizationSuccess,
        onPreAuthFailed,
        onPreAuthFailedButCardSaved,
        saveCardOnFile: fields.saveCardOnFile,
        orderId,
        amount: fields.preauthorizeAmount,
        currency: TransactionCurrency[fields.currency],
      });
    } else {
      preauthorize({
        creditCardId: fields.creditCard,
        amount: fields.preauthorizeAmount,
        currency: TransactionCurrency[fields.currency],
      });
    }
  };

  const isReadyToPreauth = useSingleUseCard
    ? isNuveiNewCreditCardFormComplete && formState.isValid
    : formState.isValid;
  const isLoading = isPreauthorizing || nuveiLoading;

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <Stack gap={theme.spacing['3']}>
        <Stack direction="row" gap={theme.spacing['3']}>
          <ControllerFormNumberInput
            control={control}
            fieldName={AmountField.fieldName}
            placeholder={t('preauthorization.enterAmount')}
          />
          <AvailableCurrencyDropdownWithOptionalDefault
            control={control}
            defaultCurrency={blockedCurrency}
            setValue={setValue}
            disabled={!!blockedCurrency}
          />
        </Stack>
        <CreditCardSelector
          control={control}
          creditCards={availableCreditCards}
          disabled={useSingleUseCard}
        />

        <Divider sx={{ marginY: '20px' }}>
          <Typography>{t('or')}</Typography>
        </Divider>

        <Toggle
          label={t('enterNewCreditCard')}
          onToggle={() => {
            setUseSingleUseCard((prev) => !prev);
            resetField(CreditCardField.fieldName);
          }}
          checked={useSingleUseCard}
        />

        <div style={!useSingleUseCard ? { display: 'none' } : undefined}>
          <NewCreditCardFormContent
            register={register}
            control={control}
            formState={formState}
            watch={watch}
          />
        </div>

        <Stack direction="row" justifyContent="end" gap={theme.spacing['3']}>
          <Button onClick={resetForm} color="error">
            {t('cancel')}
          </Button>
          <Button
            onClick={() => preauthorizePayment(getValues())}
            variant="contained"
            disabled={isLoading || !isReadyToPreauth}
          >
            {t('preauthorization.preauthorize')}
          </Button>
        </Stack>
      </Stack>
    </>
  );
};
